import React, { useEffect, useState } from "react";
import { createReport, getReport, updateReport } from "./api";
import styles from "./Form.module.css";

const REPORT_TYPES = [
  { value: "audit", label: "Audit Report" },
  { value: "annual", label: "Annual Report" },
];

const STATUS_OPTIONS = ["Active", "Inactive"];

function normalizeDate(value) {
  const trimmed = String(value || "").trim();
  const dashDate = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dashDate) {
    return `${dashDate[3]}-${dashDate[2]}-${dashDate[1]}`;
  }
  return trimmed;
}

export default function ReportsAdd() {
  const [form, setForm] = useState({
    type: "audit",
    financial_year: "",
    title: "",
    description: "",
    status: "Active",
    display_order: 0,
    publish_date: "",
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [editId, setEditId] = useState(null);
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      setEditId(id);
      getReport(id).then((res) => {
        if (res.item) {
          setForm({
            type: res.item.type || 'audit',
            financial_year: res.item.financial_year || '',
            title: res.item.title || '',
            description: res.item.description || '',
            status: res.item.status || 'Active',
            display_order: res.item.display_order || 0,
            publish_date: res.item.publish_date || '',
          });
        }
      }).catch(() => {});
    }
  }, []);

  function updateField(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setStatus("");

    const requiredFields = [
      ["financial_year", "Financial year"],
      ["title", "Report title"],
      ["description", "Description"],
      ["publish_date", "Publish date"],
    ];
    const missingField = requiredFields.find(([key]) => !String(form[key] || "").trim());
    if (missingField) {
      setStatus(`${missingField[1]} is required.`);
      return;
    }

    if (!editId && !pdfFile) {
      setStatus("Please upload a PDF report before saving.");
      return;
    }

    if (pdfFile && pdfFile.type !== "application/pdf") {
      setStatus("Only PDF files are allowed.");
      return;
    }

    if (pdfFile && pdfFile.size > 10 * 1024 * 1024) {
      setStatus("The PDF must be smaller than 10MB.");
      return;
    }

    const fd = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      const normalizedValue = key === "publish_date" ? normalizeDate(value) : value;
      fd.append(key, String(normalizedValue ?? ""));
    });
    if (pdfFile) fd.append('pdf', pdfFile);

    setSaving(true);
    try {
      if (editId) {
        await updateReport(editId, fd);
        setStatus('Report updated successfully.');
      } else {
        await createReport(fd);
        setStatus('Report created successfully.');
        setForm({ type: 'audit', financial_year: '', title: '', description: '', status: 'Active', display_order: 0, publish_date: '' });
        setPdfFile(null);
      }
    } catch (err) {
      setStatus(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.card} style={{ padding: '24px' }}>
      <h2 className={styles.title}>{editId ? 'Edit Report' : 'Add Report'}</h2>
      {status && <p className={status.toLowerCase().includes("success") ? styles.success : styles.error}>{status}</p>}
      <form className={styles.grid} onSubmit={onSubmit}>
        <div className={styles.row}>
          <div className={styles.label}>Report Type</div>
          <select className={styles.input} name="type" value={form.type} onChange={updateField} required>
            {REPORT_TYPES.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>Financial Year</div>
          <input className={styles.input} name="financial_year" value={form.financial_year} onChange={updateField} placeholder="e.g. 2026-27" required />
        </div>
        <div className={styles.row}>
          <div className={styles.label}>Report Title</div>
          <input className={styles.input} name="title" value={form.title} onChange={updateField} placeholder="Annual report title" required />
        </div>
        <div className={styles.row}>
          <div className={styles.label}>Description</div>
          <textarea className={styles.textarea} name="description" value={form.description} onChange={updateField} placeholder="Enter a short report description" required />
        </div>
        <div className={styles.row}>
          <div className={styles.label}>PDF Upload</div>
          <div>
            <input className={styles.file} type="file" name="pdf" accept="application/pdf" onChange={(e) => setPdfFile(e.target.files?.[0] || null)} required={!editId} />
            <div className={styles.hint}>
              {editId ? "Upload a new PDF only if you want to replace the current report." : "PDF file is required."}
            </div>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>Status</div>
          <select className={styles.input} name="status" value={form.status} onChange={updateField}>
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>Display Order</div>
          <input className={styles.input} type="number" min="0" name="display_order" value={form.display_order} onChange={updateField} />
        </div>
        <div className={styles.row}>
          <div className={styles.label}>Publish Date</div>
          <input className={styles.date} type="date" name="publish_date" value={form.publish_date} onChange={updateField} required />
        </div>
        <div className={styles.row} style={{ gridTemplateColumns: '1fr', gap: 0 }}>
          <div className={styles.actions}>
            <button className={styles.button} type="submit" disabled={saving}>
              {saving ? "Saving..." : editId ? "Update Report" : "Save Report"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
